/**
 * Use Case: Criar Lead
 * Orquestra a criação de uma negociação com validações de domínio.
 *
 * Importante: retorna o `Lead` com o `id` REAL persistido pelo banco
 * (substituindo o `temp_*` usado para construir a entidade), eliminando
 * a heurística de "buscar por contactId+name" no caller.
 */

import { ILeadRepository } from '../../../domain/repositories/ILeadRepository';
import { Lead } from '../../../domain/entities/Lead';
import { ValidationError, InfrastructureError } from '../../../core/errors/DomainErrors';

export interface CreateLeadDTO {
    name: string;
    email: string;
    whatsApp: string;
    value: number;
    stage: string;
    origin: string;
    agent?: string;
    tags?: string[];
    observations?: string;
    contactId: string;
    organizationId?: string;
}

export class CreateLeadUseCase {
    constructor(private readonly leadRepository: ILeadRepository) { }

    async execute(dto: CreateLeadDTO): Promise<Lead> {
        if (!dto.contactId) {
            throw new ValidationError('Contato é obrigatório para criar negociação');
        }

        const tempId = `temp_${Date.now()}`;
        const now = new Date();

        const draft = new Lead(
            tempId,
            dto.name,
            dto.email,
            dto.whatsApp,
            dto.value,
            dto.stage,
            dto.origin,
            'open',
            dto.agent || null,
            dto.tags || [],
            dto.observations || '',
            now,
            dto.contactId,
            undefined,
            undefined,
            now
        );

        const persistedId = await this.leadRepository.save(draft, dto.organizationId);

        if (!persistedId) {
            throw new InfrastructureError('Negociação não retornou ID após criação');
        }

        const reloaded = await this.leadRepository.findById(persistedId);
        if (reloaded) {
            return reloaded;
        }

        return new Lead(
            persistedId,
            draft.name,
            draft.getEmail(),
            draft.getWhatsApp(),
            draft.getValue(),
            draft.stage,
            draft.origin,
            draft.status,
            draft.agent,
            draft.tags,
            draft.observations,
            draft.entryDate,
            draft.contactId,
            draft.createdBy,
            draft.creatorName,
            draft.stageEntryDate,
            draft.wonAt,
            draft.lostAt,
            draft.lostReasonId,
            draft.lossComments
        );
    }
}
